import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { beneficiariesApi } from '../api/modules';
import { StatusPill } from '../components/ui/StatusPill';
import { BeneficiaryDrawer } from '../drawers/BeneficiaryDrawer';

const STAGES = [
  { key: 'application',      label: 'Application' },
  { key: 'lottery_selected', label: 'Lottery selected' },
  { key: 'declaration',      label: 'Declaration' },
  { key: 'deposit_verified', label: 'Deposit verified' },
  { key: 'subsidy_released', label: 'Subsidy released' },
  { key: 'verified',         label: 'Verified' },
];

export function BeneficiariesPage() {
  const [scheme, setScheme] = useState<'GOAT' | 'SHEEP' | 'PIG' | 'any'>('any');
  const [stage, setStage] = useState<string>('any');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ['ben-summary'], queryFn: beneficiariesApi.summary });
  const list = useQuery({
    queryKey: ['ben-list', { scheme, stage, page, search }],
    queryFn: () => beneficiariesApi.list({ page, page_size: 25, scheme, stage, search: search || undefined }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <Header breadcrumbs={['Modules', 'Beneficiaries']} />
      <div className="px-8 pt-7 pb-2">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Module M02</div>
            <h1 className="serif-display text-3xl text-navy-800 font-medium">Beneficiaries</h1>
            <p className="text-sm text-navy-800/60 mt-1">Small Animal schemes — Goat, Sheep, Pig · Lifecycle management</p>
          </div>
          <input
            type="text"
            placeholder="Search by name or ID…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="text-sm px-3 py-2 border border-cream-300 rounded-sm bg-white w-72 focus:outline-none focus:border-saffron-500"
          />
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Filter chips */}
        <div className="bg-white border border-cream-200 rounded-sm p-4 flex items-center gap-3 flex-wrap text-xs">
          <span className="text-navy-800/60">Schemes:</span>
          {([['any', 'All'], ['GOAT', 'Goat'], ['SHEEP', 'Sheep'], ['PIG', 'Pig']] as const).map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setScheme(k); setPage(1); }}
              className={`px-3 py-1.5 rounded-sm transition ${
                scheme === k ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'
              }`}
            >
              {lbl}
            </button>
          ))}
          <span className="text-cream-300 mx-1">|</span>
          <span className="text-navy-800/60">Stage:</span>
          <button
            type="button"
            onClick={() => { setStage('any'); setPage(1); }}
            className={`px-3 py-1.5 rounded-sm transition ${stage === 'any' ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
          >
            All
          </button>
          {STAGES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => { setStage(s.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-sm transition ${stage === s.key ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-6 gap-3">
          {STAGES.map((s) => (
            <div key={s.key} className="bg-white border border-cream-200 p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-navy-800/50">{s.label}</div>
              <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">
                {summary.data ? (summary.data.stages[s.key] ?? 0).toLocaleString('en-IN') : '…'}
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-cream-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-cream-100 flex items-center justify-between text-xs">
            <span className="text-navy-800/60">
              {list.data
                ? `Showing ${list.data.data.length} of ${list.data.pagination.total.toLocaleString('en-IN')} · sorted by application date`
                : 'Loading…'}
            </span>
            {list.data && (
              <span className="text-navy-800/60 font-numeric">
                Page {list.data.pagination.page} of {list.data.pagination.total_pages}
              </span>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50">
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">ID</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Category</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Scheme</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">District / Block</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Status</th>
                <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Amount</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Applied</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading || !list.data ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-t border-cream-100"><td colSpan={8} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                ))
              ) : (
                list.data.data.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => setSelected(b.id)}
                    className="border-t border-cream-100 hover:bg-cream-50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{b.id}</td>
                    <td className="px-3 py-2.5 font-medium text-navy-800">{b.name}</td>
                    <td className="px-3 py-2.5"><span className="text-[11px] font-medium text-navy-800/60">{b.category}</span></td>
                    <td className="px-3 py-2.5 text-navy-800/80">{b.scheme}</td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs">{b.district}{b.block && ` · ${b.block}`}</td>
                    <td className="px-3 py-2.5"><StatusPill status={b.status} /></td>
                    <td className="px-3 py-2.5 text-right font-numeric">{b.amount_inr > 0 ? '₹' + b.amount_inr.toLocaleString('en-IN') : '—'}</td>
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">
                      {new Date(b.applied_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {list.data && (
            <div className="px-5 py-3 border-t border-cream-100 flex items-center justify-end gap-2 text-xs">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 bg-cream-100 text-navy-800/80 rounded-sm hover:bg-cream-200 disabled:opacity-50"
              >
                ← Previous
              </button>
              <button
                type="button"
                disabled={page >= list.data.pagination.total_pages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 bg-navy-800 text-white rounded-sm hover:bg-navy-700 disabled:opacity-50"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      <BeneficiaryDrawer open={!!selected} code={selected} onClose={() => setSelected(null)} />
    </>
  );
}
